import Auth from 'dashboard/api/auth';
import { FileChecksum } from 'activestorage/src/file_checksum';
import { BlobRecord } from 'activestorage/src/blob_record';
import { BlobUpload } from 'activestorage/src/blob_upload';

const notify = (target, method, ...args) => target?.[method]?.(...args);
const REUSE_LOOKUP_HEADER = 'X-Virtuazza-Reuse-Lookup';

const checksumFile = file =>
  new Promise((resolve, reject) => {
    FileChecksum.create(file, (error, checksum) => {
      if (error) reject(new Error(error));
      else resolve(checksum);
    });
  });

const sha256File = async file => {
  if (file.type.startsWith('audio/') || !globalThis.crypto?.subtle) return null;

  try {
    const digest = await globalThis.crypto.subtle.digest(
      'SHA-256',
      await file.arrayBuffer()
    );
    return [...new Uint8Array(digest)]
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
};

export class AccountScopedDirectUpload {
  constructor(file, url, delegate) {
    this.file = file;
    this.url = url;
    this.delegate = delegate;
  }

  create(callback) {
    checksumFile(this.file)
      .then(checksum => this.createBlob(checksum, callback))
      .catch(error => callback(error.message));
  }

  createBlob(checksum, callback, sha256 = null, allowLookup = true) {
    const blob = new BlobRecord(this.file, checksum, this.url);
    if (sha256) {
      blob.attributes.metadata = { virtuazza_sha256_claim: sha256 };
    } else if (
      allowLookup &&
      !this.file.type.startsWith('audio/') &&
      globalThis.crypto?.subtle
    ) {
      blob.xhr.setRequestHeader(REUSE_LOOKUP_HEADER, '1');
    }
    notify(this.delegate, 'directUploadWillCreateBlobWithXHR', blob.xhr);
    blob.create(error => this.onBlobCreated(error, blob, checksum, callback));
  }

  onBlobCreated(error, blob, checksum, callback) {
    if (error) return callback(error);
    const attributes = blob.toJSON();
    if (attributes.reuse_candidate) {
      return sha256File(this.file)
        .then(sha256 => this.createBlob(checksum, callback, sha256, false))
        .catch(() => this.createBlob(checksum, callback, null, false));
    }
    if (attributes.reused) return callback(null, attributes);

    const upload = new BlobUpload(blob);
    notify(this.delegate, 'directUploadWillStoreFileWithXHR', upload.xhr);
    return upload.create(uploadError =>
      callback(uploadError, uploadError ? undefined : attributes)
    );
  }
}

export const setDirectUploadAuthHeaders = xhr => {
  const {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  } = Auth.getAuthData() || {};

  if (!accessToken) return;

  xhr.setRequestHeader('access-token', accessToken);
  xhr.setRequestHeader('token-type', tokenType);
  xhr.setRequestHeader('client', client);
  xhr.setRequestHeader('expiry', expiry);
  xhr.setRequestHeader('uid', uid);
};
